/**
 * Validates constraint definitions to ensure bidirectional consistency:
 * If Node A lists Node B as an accepted parent, then Node B should list Node A as an accepted child.
 * Handles the special case where a parent type isn't defined in constraints (accepts any child).
 *
 * @param {Object} constraints - The constraints object to validate
 * @param {Array|Object} allowedReaderConnectorTypes - Array or object of reader connector types
 * @param {boolean} autoFix - Whether to automatically fix inconsistencies (default: true)
 * @returns {Object} - Object containing errors found and fixed constraints if autoFix is true
 */
function validateConstraintConsistency({
  constraints,
  allowedReaderConnectorTypes,
  autoFix = true,
}) {
  const errors = [];
  const fixedConstraints = autoFix ? JSON.parse(JSON.stringify(constraints)) : null;
  const readerTypes = Array.isArray(allowedReaderConnectorTypes)
    ? allowedReaderConnectorTypes
    : Object.values(allowedReaderConnectorTypes);

  // Check each node type in the constraints
  Object.keys(constraints).forEach((nodeType) => {
    const constraint = constraints[nodeType];

    // For each parent that this node accepts
    constraint.acceptedParentNodes.forEach((parentNodeType) => {
      // Skip validation for reader connector types if they're not explicitly defined in constraints
      if (readerTypes.includes(parentNodeType) && !constraints[parentNodeType]) {
        return;
      }

      // Skip validation for parent types that aren't defined in constraints (can accept any child)
      if (!constraints[parentNodeType]) {
        return;
      }

      // Check if this node is listed as an accepted child in the parent's constraints
      const parentConstraint = constraints[parentNodeType];

      if (!parentConstraint.acceptedChildNodes.includes(nodeType)) {
        errors.push({
          type: 'Missing child reference',
          parent: parentNodeType,
          expectedChild: nodeType,
        });

        // Auto-fix if enabled
        if (autoFix) {
          fixedConstraints[parentNodeType].acceptedChildNodes.push(nodeType);
        }
      }
    });
  });

  return {
    errors,
    fixedConstraints: autoFix ? fixedConstraints : null,
  };
}

export default validateConstraintConsistency;
